import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { MediaManager } from "../../../components/MediaManager/MediaManager";
import TextForm from "../../../components/TextForm/TextForm";
import { validateFields } from "../../../utils/contentBlacklistValidator";
import { useBotCurrency } from "../../../hooks/useBotCurrency";

const COLOR_OPTIONS = [
    { label: "Padrão", value: "default", class: "bg-gray-600" },
    { label: "Vermelho", value: "red", class: "bg-red-600" },
    { label: "Verde", value: "green", class: "bg-green-600" },
    { label: "Azul", value: "blue", class: "bg-blue-600" },
];

/**
 * Order Bump PERSONALIZADO — reutilizável para qualquer oferta-pai
 * (upsell, downsell, disparo). Fala com a rota genérica
 * /api/bots/order_bump/offer/<offerId>.
 *
 * SEM botão próprio: é salvo JUNTO com o "Salvar Upsell/Downsell/Disparo". O
 * formulário-pai controla via ref: chama `validate()` antes de criar a oferta e
 * `save(parentOfferId)` depois de a oferta existir. Se a criação da oferta
 * falhar, o pai simplesmente não chama save — nenhum bump é criado.
 *
 * Props:
 * - botId: bot para contexto de mídia/moeda (no disparo multi-bot, um dos bots).
 * - offerId: id da oferta-pai (pode ser null na criação — os campos aparecem
 *   mesmo assim; a persistência acontece no save do pai, via ref).
 */
const PersonalizedOrderBump = forwardRef(function PersonalizedOrderBump({
    botId,
    offerId,
    uploadContext = "orderbump",
}, ref) {
    const { showError } = useNotification();
    const { currencyLabel, currencySymbol } = useBotCurrency(botId);
    const mediaRef = useRef();

    const [bumpOfferId, setBumpOfferId] = useState(null);
    const [offerName, setOfferName] = useState("");
    const [message, setMessage] = useState("");
    const [deliverableMessage, setDeliverableMessage] = useState("");
    const [acceptText, setAcceptText] = useState("");
    const [declineText, setDeclineText] = useState("");
    const [acceptButtonColor, setAcceptButtonColor] = useState("default");
    const [declineButtonColor, setDeclineButtonColor] = useState("default");
    const [price, setPrice] = useState(0);
    const [isActive, setIsActive] = useState(false);

    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState("agrupado");

    useEffect(() => {
        if (!offerId) return;
        loadOrderBump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerId]);

    const loadOrderBump = async () => {
        try {
            const { data } = await authService.order_bump.get_personalized_order_bump(offerId);
            if (!data.success) return;

            const ob = data.order_bump || {};
            setBumpOfferId(ob.id || null);
            setOfferName(ob.name || "");
            setMessage(ob.message || "");
            setDeliverableMessage(ob.deliverable_message || "");
            setAcceptText(ob.accept_button_text || "");
            setDeclineText(ob.decline_button_text || "");
            setAcceptButtonColor(ob.accept_button_color || "default");
            setDeclineButtonColor(ob.decline_button_color || "default");
            setPrice(ob.price ?? 0);
            setIsActive(ob.is_active || false);
            setSendMode(ob.media_send_mode || "agrupado");

            if (ob.media_list && ob.media_list.length > 0) {
                setMediaList(ob.media_list);
            } else {
                const legacy = [];
                if (ob.image_file_id) legacy.push({ media_type: "photo", file_id: ob.image_file_id, thumbnail_file_id: null });
                if (ob.video_file_id) legacy.push({ media_type: "video", file_id: ob.video_file_id, thumbnail_file_id: null });
                if (ob.audio_file_id) legacy.push({ media_type: "voice", file_id: ob.audio_file_id, thumbnail_file_id: null });
                setMediaList(legacy);
            }
        } catch (err) {
            if (err?.response?.status !== 404) {
                showError("Erro ao carregar Order Bump personalizado");
            }
        }
    };

    // Valida só quando ATIVO (desativado = ignorado). Retorna erro (string) ou null.
    const validate = () => {
        if (!isActive) return null;

        // Campos obrigatórios do Order Bump.
        const required = [
            ["Nome da Oferta", offerName],
            ["Mensagem do Order Bump", message],
            ["Mensagem do Entregável", deliverableMessage],
            ["Texto do botão aceitar", acceptText],
            ["Texto do botão recusar", declineText],
        ];
        for (const [label, value] of required) {
            if (!String(value ?? "").trim()) return `Order Bump: preencha "${label}".`;
        }

        // Preço deve ser um número ≥ 2 (mesmo mínimo dos planos).
        const p = Number(price);
        if (!Number.isFinite(p) || p < 2) {
            return "Order Bump: o preço deve ser um número maior ou igual a 2.";
        }

        const check = validateFields([
            { label: "Nome da Oferta (Order Bump)", value: offerName },
            { label: "Mensagem do Order Bump", value: message },
            { label: "Mensagem do Entregável (Order Bump)", value: deliverableMessage },
            { label: "Texto do botão aceitar (Order Bump)", value: acceptText },
            { label: "Texto do botão recusar (Order Bump)", value: declineText },
        ]);
        return check.isValid ? null : check.error;
    };

    // Persiste o bump na oferta-pai `parentOfferId` (ou no offerId atual). Se estiver
    // desativado e nunca existiu, é no-op. Retorna o id do bump salvo (ou null).
    const save = async (parentOfferId) => {
        const target = parentOfferId || offerId;
        if (!target) return null;
        if (!isActive && !bumpOfferId) return null;

        const payload = {
            name: offerName,
            message,
            deliverable_message: deliverableMessage,
            accept_button_text: acceptText,
            decline_button_text: declineText,
            accept_button_color: acceptButtonColor,
            decline_button_color: declineButtonColor,
            price: Number(price),
            is_active: isActive,
            media_send_mode: sendMode,
        };

        const { data } = await authService.order_bump.save_personalized_order_bump(target, payload);
        if (!data.success) {
            throw new Error(data.message || "Erro ao salvar Order Bump personalizado");
        }
        const savedId = data.offer_id || bumpOfferId;
        setBumpOfferId(savedId);
        if (mediaRef.current && savedId) {
            await mediaRef.current.uploadAll(savedId);
        }
        return savedId;
    };

    useImperativeHandle(ref, () => ({ isActive: () => isActive, validate, save }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isActive, offerName, message, deliverableMessage, acceptText, declineText,
            acceptButtonColor, declineButtonColor, price, sendMode, bumpOfferId, offerId]);

    return (
        <div className="ob-card">
            <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-gift text-[#ec4899] text-sm" aria-hidden="true" />
                        <h3 className="font-semibold text-white text-sm">Order Bump desta oferta</h3>
                    </div>
                    <label className="toggle-wrapper toggle-sm">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                        <span className="toggle-slider" />
                    </label>
                </div>
                <p className="text-[11px] text-white/40 mb-3">
                    Oferta extra exibida ao cliente no momento do pagamento.
                </p>

                {isActive && (
                    <>
                        <div className="mt-4">
                        <label className="spt-label">Nome da Oferta <span className="required">*</span></label>
                        <input
                            value={offerName}
                            onChange={(e) => setOfferName(e.target.value)}
                            className="input3"
                            placeholder="Ex: Bônus exclusivo desta oferta"
                            required
                        />
                    </div>

                    <div className="mt-8">
                        <MediaManager
                            ref={mediaRef}
                            botId={botId}
                            targetType="offer"
                            targetId={bumpOfferId}
                            multiMedia={true}
                            mediaList={mediaList}
                            onMediaChange={setMediaList}
                            sendMode={sendMode}
                            onSendModeChange={setSendMode}
                            uploadContext={uploadContext}
                        />
                    </div>

                    <div className="mt-8">
                        <label className="spt-label">Mensagem do Order Bump <span className="required">*</span></label>
                        <TextForm
                            value={message}
                            onChange={setMessage}
                            placeholder="🎁 Digite o texto da mensagem do Order Bump aqui..."
                            hint="Mensagem que será exibida ao cliente, apresentando a oferta adicional."
                            showVariables={false}
                        />
                    </div>

                    <div className="mt-8">
                        <label className="spt-label">Preço {currencyLabel}<span className="text-white/60 text-sm ml-1">{currencySymbol}</span> <span className="required">*</span></label>
                        <input
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="input3"
                            min="2"
                            step="0.01"
                            required
                        />
                    </div>

                    <div className="block md:flex w-full gap-8 mt-8">
                        <div className="w-full">
                            <label className="spt-label">Texto do botão recusar <span className="required">*</span></label>
                            <input
                                value={declineText}
                                onChange={(e) => setDeclineText(e.target.value)}
                                className="input3"
                                placeholder="🔴RECUSO🔴"
                                required
                            />
                            <div className="mx-auto mt-4 w-max flex items-center gap-2 md:mx-0">
                                {COLOR_OPTIONS.map((c) => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        title={c.label}
                                        onClick={() => setDeclineButtonColor(c.value)}
                                        className={`w-5 h-5 rounded-full border-2 transition-all ${c.class} ${declineButtonColor === c.value ? "border-white scale-110" : "border-white/20"}`}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="w-full">
                            <label className="spt-label">Texto do botão aceitar <span className="required">*</span></label>
                            <input
                                value={acceptText}
                                onChange={(e) => setAcceptText(e.target.value)}
                                className="input3"
                                placeholder="🟢ACEITO🟢"
                                required
                            />
                            <div className="mx-auto mt-4 w-max flex items-center gap-2 md:mx-0">
                                {COLOR_OPTIONS.map((c) => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        title={c.label}
                                        onClick={() => setAcceptButtonColor(c.value)}
                                        className={`w-5 h-5 rounded-full border-2 transition-all ${c.class} ${acceptButtonColor === c.value ? "border-white scale-110" : "border-white/20"}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <label className="spt-label">Mensagem do Entregável <span className="required">*</span></label>
                        <textarea
                            rows={4}
                            value={deliverableMessage}
                            onChange={(e) => setDeliverableMessage(e.target.value)}
                            className="spt-textarea"
                            placeholder={`🎉Parabéns! Aqui está o seu bônus exclusivo:\n\n📌Link de acesso: https://t.me/+abcdEFG12345\n📌Aproveite!`}
                            required
                        />
                    </div>
                    </>
                )}
            </div>
        </div>
    );
});

export default PersonalizedOrderBump;
